import React, { useState, useEffect } from 'react';
import { Plus, Minus, Edit3, Check, X, DollarSign, Sparkles } from 'lucide-react';
import { BudgetBreakdown, BudgetCategory } from './types';

interface BudgetDonutProps {
  budget: number;
  categories: BudgetCategory[];
  selectedCategory: BudgetCategory | null;
  onSelectCategory: (category: BudgetCategory | null) => void;
  onUpdateBudget: (newTotal: number) => void;
  onTotalBudgetChange: (newTotal: number) => void;
}

export const BudgetDonut: React.FC<BudgetDonutProps> = ({
  budget,
  categories,
  selectedCategory,
  onSelectCategory,
  onUpdateBudget,
  onTotalBudgetChange,
}) => {
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const [isEditingBudget, setIsEditingBudget] = useState(false);
  const [tempBudget, setTempBudget] = useState<string>(budget.toString());

  useEffect(() => {
    setTempBudget(budget.toString());
  }, [budget]);

  const handleBudgetSubmit = async () => {
    const newBudget = Math.max(0, parseInt(tempBudget.replace(/,/g, '')) || 0);
    await onTotalBudgetChange(newBudget);
    setIsEditingBudget(false);
  };

  const handleBudgetCancel = () => {
    setTempBudget(budget.toString());
    setIsEditingBudget(false);
  };

  const handleQuickAdjustment = async (amount: number) => {
    const newBudget = Math.max(0, budget + amount);
    await onTotalBudgetChange(newBudget);
  };

  const handleCategoryClick = (category: BudgetCategory) => {
    console.log('Category clicked:', category.name);
    onSelectCategory(selectedCategory?.id === category.id ? null : category);
  };

  // Enhanced deduplication with logging
  const uniqueCategories = categories.reduce((acc: BudgetCategory[], category) => {
    const existsById = acc.find(c => c.id === category.id);
    const existsByName = acc.find(c => c.name === category.name);
    
    if (existsById || existsByName) {
      console.log('💍 Duplicate category filtered out:', category.name, category.id, 'existing:', existsByName?.id || existsById?.id);
      return acc;
    }
    
    if (!category.id || !category.name || !category.color || category.percentage <= 0) {
      console.log('💍 Invalid category filtered out:', {
        id: category.id,
        name: category.name,
        color: category.color,
        percentage: category.percentage
      });
      return acc;
    }
    
    console.log('💍 Valid category added:', {
      id: category.id,
      name: category.name,
      amount: category.amount,
      percentage: category.percentage.toFixed(1) + '%'
    });
    
    return [...acc, category];
  }, []);

  const size = 400;
  const centerX = size / 2;
  const centerY = size / 2 + 20; // Slightly lower to accommodate diamond
  const ringRadius = 110;
  const strokeWidth = 50;
  const circumference = 2 * Math.PI * ringRadius;

  // Calculate curved ring segments (back to original circular approach)
  let cumulativePercentage = 0;
  const gapPercentage = 0.5; // Small gap between segments
  
  const ringSegments = uniqueCategories.map((category, index) => {
    const startPercentage = cumulativePercentage + (index > 0 ? gapPercentage : 0);
    const adjustedPercentage = Math.max(0, category.percentage - gapPercentage);
    cumulativePercentage += category.percentage;
    
    const strokeDasharray = `${(adjustedPercentage / 100) * circumference} ${circumference}`;
    const strokeDashoffset = -((startPercentage / 100) * circumference);

    return {
      ...category,
      strokeDasharray,
      strokeDashoffset,
      adjustedPercentage,
    };
  });

  // Enhanced debug logging
  const totalPercentage = uniqueCategories.reduce((sum, cat) => sum + cat.percentage, 0);
  const totalAmount = uniqueCategories.reduce((sum, cat) => sum + cat.amount, 0);
  
  console.log('💍 === Diamond Ring Budget Summary ===');
  console.log('💍 Input categories:', categories.length);
  console.log('💍 Unique categories after filtering:', uniqueCategories.length);
  console.log('💍 Ring segments rendered:', ringSegments.length);
  console.log('💍 Total percentage:', totalPercentage.toFixed(1) + '%');
  console.log('💍 Total amount:', '$' + totalAmount.toLocaleString());

  return (
    <div className="flex flex-col items-center justify-center space-y-8">
      {/* Diamond Ring Visualization */}
      <div className="relative">
        {/* Diamond positioned absolutely on top (outside rotated SVG) */}
        <div className="absolute inset-0 pointer-events-none z-10">
          <svg 
            width={size} 
            height={size} 
            className="drop-shadow-xl"
            style={{ overflow: 'visible' }}
          >
            {/* Heart Diamond on Top */}
            <g transform={`translate(${centerX}, ${centerY - 85})`}>
              {/* Heart Shadow */}
              <path
                d="M 0,8 C -8,0 -16,0 -16,12 C -16,20 -8,28 0,36 C 8,28 16,20 16,12 C 16,0 8,0 0,8 Z"
                fill="rgba(0, 0, 0, 0.15)"
                className="blur-sm"
                transform="translate(2, 3)"
              />
              
              {/* Heart Diamond Base */}
              <path
                d="M 0,8 C -8,0 -16,0 -16,12 C -16,20 -8,28 0,36 C 8,28 16,20 16,12 C 16,0 8,0 0,8 Z"
                fill="url(#heartDiamondGradient)"
                stroke="rgba(255, 255, 255, 0.9)"
                strokeWidth="1.5"
                className="drop-shadow-lg"
              />
              
              {/* Heart Highlight */}
              <path
                d="M 0,8 C -6,2 -12,2 -12,10 C -12,16 -6,22 0,26 C 6,22 12,16 12,10 C 12,2 6,2 0,8 Z"
                fill="rgba(255, 255, 255, 0.4)"
                className="animate-pulse"
                style={{ animationDuration: '3s' }}
              />
              
              {/* Heart Inner Sparkle */}
              <circle cx="-4" cy="12" r="1.5" fill="rgba(255, 255, 255, 0.9)" className="animate-ping" style={{ animationDelay: '0s', animationDuration: '2.5s' }} />
              <circle cx="4" cy="12" r="1.5" fill="rgba(255, 255, 255, 0.9)" className="animate-ping" style={{ animationDelay: '1.2s', animationDuration: '2.5s' }} />
              <circle cx="0" cy="18" r="1" fill="white" className="animate-ping" style={{ animationDelay: '0.8s', animationDuration: '2s' }} />
            </g>

            {/* Heart Ring Prongs */}
            <g>
              <line x1={centerX - 10} y1={centerY - 55} x2={centerX - 14} y2={centerY - 70} stroke="url(#ringGradient)" strokeWidth="3" strokeLinecap="round" className="drop-shadow-md" />
              <line x1={centerX + 10} y1={centerY - 55} x2={centerX + 14} y2={centerY - 70} stroke="url(#ringGradient)" strokeWidth="3" strokeLinecap="round" className="drop-shadow-md" />
              <line x1={centerX} y1={centerY - 50} x2={centerX} y2={centerY - 65} stroke="url(#ringGradient)" strokeWidth="2" strokeLinecap="round" className="drop-shadow-md" />
            </g>

            {/* Heart Diamond Gradients */}
            <defs>
              <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#E5E7EB" />
                <stop offset="50%" stopColor="#F9FAFB" />
                <stop offset="100%" stopColor="#D1D5DB" />
              </linearGradient>
              <radialGradient id="heartDiamondGradient" cx="50%" cy="40%" r="80%">
                <stop offset="0%" stopColor="rgba(255, 182, 193, 0.9)" />
                <stop offset="30%" stopColor="rgba(255, 105, 180, 0.8)" />
                <stop offset="60%" stopColor="rgba(219, 39, 119, 0.6)" />
                <stop offset="100%" stopColor="rgba(147, 51, 234, 0.4)" />
              </radialGradient>
            </defs>
          </svg>
        </div>

        {/* Ring Band SVG (rotated) */}
        <svg 
          width={size} 
          height={size} 
          className="transform -rotate-90 drop-shadow-xl"
          style={{ overflow: 'visible' }}
        >
          {/* Ring Shadow */}
          <ellipse
            cx={centerX}
            cy={centerY + 15}
            rx={ringRadius + strokeWidth/2 + 5}
            ry={15}
            fill="rgba(0, 0, 0, 0.1)"
            className="blur-sm"
          />

          {/* Ring Base (Background circle) */}
          <circle
            cx={centerX}
            cy={centerY}
            r={ringRadius}
            fill="none"
            stroke="rgba(255, 255, 255, 0.15)"
            strokeWidth={strokeWidth}
            className="backdrop-blur-sm"
          />

          {/* Budget Category Segments (Curved) */}
          {ringSegments.map((segment, index) => {
            const isSelected = selectedCategory?.id === segment.id;
            const isHovered = hoveredCategory === segment.id;
            
            return (
              <g key={segment.id}>
                {/* Main curved segment */}
                <circle
                  cx={centerX}
                  cy={centerY}
                  r={ringRadius}
                  fill="none"
                  stroke={segment.color}
                  strokeWidth={strokeWidth - 4}
                  strokeDasharray={segment.strokeDasharray}
                  strokeDashoffset={segment.strokeDashoffset}
                  className={`transition-all duration-300 cursor-pointer ${
                    isSelected || isHovered 
                      ? 'brightness-110 saturate-110' 
                      : 'opacity-90 hover:opacity-100'
                  }`}
                  style={{
                    strokeLinecap: 'round',
                    filter: isSelected || isHovered ? `drop-shadow(0 0 15px ${segment.color}80) brightness(1.1)` : 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                    pointerEvents: 'all',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCategoryClick(segment);
                  }}
                  onMouseEnter={() => setHoveredCategory(segment.id)}
                  onMouseLeave={() => setHoveredCategory(null)}
                />
                
                {/* Highlight glow for selected/hovered */}
                {(isSelected || isHovered) && (
                  <circle
                    cx={centerX}
                    cy={centerY}
                    r={ringRadius}
                    fill="none"
                    stroke={segment.color}
                    strokeWidth={strokeWidth + 8}
                    strokeDasharray={segment.strokeDasharray}
                    strokeDashoffset={segment.strokeDashoffset}
                    className="opacity-30 animate-pulse pointer-events-none"
                    style={{
                      strokeLinecap: 'round',
                      filter: `drop-shadow(0 0 10px ${segment.color})`,
                    }}
                  />
                )}
              </g>
            );
          })}


        </svg>

        {/* Enhanced Center Content */}
        <div 
          className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
          style={{ 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)',
            width: `${(ringRadius - strokeWidth/2) * 1.6}px`,
            height: `${(ringRadius - strokeWidth/2) * 1.6}px`
          }}
        >
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-4 shadow-2xl border border-white/40 hover:bg-white/80 transition-all duration-300 group pointer-events-auto">
            <div className="text-center space-y-2">
              {/* Budget Display/Edit */}
              {isEditingBudget ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-center space-x-1">
                    <DollarSign className="w-4 h-4 text-pink-600" />
                    <input
                      type="text"
                      value={tempBudget}
                      onChange={(e) => setTempBudget(e.target.value)}
                      className="text-lg font-bold text-center bg-transparent border-b-2 border-pink-300 focus:border-pink-500 focus:outline-none w-24 bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent"
                      autoFocus
                    />
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <button
                      onClick={handleBudgetSubmit}
                      className="p-1.5 bg-gradient-to-r from-green-500/20 to-emerald-500/20 hover:from-green-500/30 hover:to-emerald-500/30 rounded-lg transition-all duration-200"
                    >
                      <Check className="w-3 h-3 text-green-600" />
                    </button>
                    <button
                      onClick={handleBudgetCancel}
                      className="p-1.5 bg-gradient-to-r from-red-500/20 to-pink-500/20 hover:from-red-500/30 hover:to-pink-500/30 rounded-lg transition-all duration-200"
                    >
                      <X className="w-3 h-3 text-red-600" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="flex items-center justify-center space-x-1">
                    <Sparkles className="w-3 h-3 text-pink-500 animate-pulse" />
                    <span 
                      className="text-lg font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent cursor-pointer hover:from-pink-600 hover:to-orange-500 transition-all duration-300"
                      onClick={() => setIsEditingBudget(true)}
                    >
                      {budget.toLocaleString()}
                    </span>
                    <button
                      onClick={() => setIsEditingBudget(true)}
                      className="p-1 opacity-60 hover:opacity-100 transition-opacity"
                    >
                      <Edit3 className="w-2.5 h-2.5 text-pink-600" />
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 font-medium">Wedding Budget</div>
                  <div className="text-xs text-pink-600 bg-pink-50 px-2 py-0.5 rounded-full">
                    {uniqueCategories.length} categories
                  </div>
                  {/* Show duplicate detection indicator */}
                  {categories.length !== uniqueCategories.length && (
                    <div className="text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full border border-orange-200">
                      {categories.length - uniqueCategories.length} duplicates cleaned
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Hover Tooltip */}
        {hoveredCategory && (
          <div className="absolute -top-40 left-1/2 transform -translate-x-1/2 bg-white/95 backdrop-blur-xl px-4 py-3 rounded-2xl shadow-xl text-sm font-medium text-gray-800 border border-white/40 animate-fadeIn pointer-events-none z-30">
            <div className="flex items-center space-x-2 mb-1">
              <Sparkles className="w-3 h-3 text-pink-500" />
              <span className="font-semibold">{uniqueCategories.find(c => c.id === hoveredCategory)?.name}</span>
            </div>
            <div className="text-xs text-gray-600">
              ${Math.round((uniqueCategories.find(c => c.id === hoveredCategory)?.percentage || 0) / 100 * budget).toLocaleString()}
            </div>
            <div className="text-xs text-pink-600 font-medium">
              {uniqueCategories.find(c => c.id === hoveredCategory)?.percentage.toFixed(1)}% of total
            </div>
          </div>
        )}
      </div>

      {/* Quick Budget Adjustment */}
      <div className="flex items-center space-x-6 bg-white/50 backdrop-blur-xl rounded-2xl p-4 border border-white/40 shadow-lg">
        <button
          onClick={() => handleQuickAdjustment(-1000)}
          className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-red-500/20 to-pink-500/20 hover:from-red-500/30 hover:to-pink-500/30 rounded-xl transition-all duration-200 group"
        >
          <Minus className="w-4 h-4 text-red-600 group-hover:scale-110 transition-transform" />
          <span className="text-sm font-medium text-red-700">$1K</span>
        </button>
        
        <div className="flex items-center space-x-2">
          <Sparkles className="w-4 h-4 text-pink-500" />
          <span className="text-sm text-gray-600 font-medium">Adjust Ring Value</span>
        </div>
        
        <button
          onClick={() => handleQuickAdjustment(1000)}
          className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-green-500/20 to-emerald-500/20 hover:from-green-500/30 hover:to-emerald-500/30 rounded-xl transition-all duration-200 group"
        >
          <Plus className="w-4 h-4 text-green-600 group-hover:scale-110 transition-transform" />
          <span className="text-sm font-medium text-green-700">$1K</span>
        </button>
      </div>
    </div>
  );
};